import Lenis from 'lenis';
import AOS from 'aos';
import { addClass, getElement, getElements, getOffset } from '../helpers/functions';
import { baseElements } from '../helpers/constants';
const {
  $header,
  $html
} = baseElements;

/**
 * Singleton instance of Lenis scroll
 * Ensures only one scroll instance exists throughout the application
 * @type {Lenis|null}
 */
let scrollInstance = null;

/**
 * Initializes the smooth scroll functionality
 * @param {Object} options - Configuration options for Lenis
 * @param {boolean} [options.autoRaf=true] - Whether to auto-handle requestAnimationFrame
 * @param {boolean} [options.smoothWheel=true] - Enable smooth scrolling for mouse wheel
 * @returns {Lenis} The Lenis scroll instance
 */
export const initScroll = (options = {}) => {
  const savedPosition = sessionStorage.getItem('scrollPosition');
  const scrollToHash = (hash, nativeScroll = false) => {
    if (!hash) return;
    const target = getElement(hash);
    if (target) {
      const targetRect = target.getBoundingClientRect();
      const isTargetAbove = targetRect.top <= $header.offsetHeight * 1.2;
      const isTab = target.closest('.tabs');
      const finalTarget = isTab ? target.closest('.frame') : target;

      // If scrolling up to a target that is already partially visible,
      // we don't need to add the header offset.
      const offset = isTargetAbove ? -$header.offsetHeight : 0;
      const {
        top
      } = getOffset(finalTarget);
      if (nativeScroll) {
        window.scrollTo(0, top + offset);
        return;
      }
      scrollInstance.scrollTo(top, {
        offset,
        easing: t => t < 0.5 ? 16 * t * t * t * t * t : 1 - (-2 * t + 2) ** (5 / 2)
      });
    }
  };
  const handleHashChange = e => {
    e.preventDefault();
    const newHash = window.location.hash;
    if (newHash) {
      scrollToHash(newHash);
    } else {
      scrollInstance.scrollTo(0);
    }
  };
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  };

  // The Usercentrics consent banner is injected at runtime into its own
  // #usercentrics-root (a shadow DOM whose events retarget to the host), so we
  // can't tag it with data-lenis-prevent. Exclude it — and any caller-provided
  // prevent — so it and its dialog scroll natively instead of via Lenis.
  const prevent = node => !!node?.closest?.('#usercentrics-root') || typeof options.prevent === 'function' && options.prevent(node);
  if (!scrollInstance) {
    // Initialize Lenis with default and custom options
    scrollInstance = new Lenis({
      autoRaf: true,
      smoothWheel: true,
      ...options,
      prevent
    });
    if (savedPosition) {
      addClass($html, '__save-scroll-position');
      scrollInstance.scrollTo(parseInt(savedPosition, 10), {
        immediate: true // Jumps directly to the position without animation
      });
      sessionStorage.removeItem('scrollPosition');
    }
    AOS.init({
      delay: 50,
      disable: 'mobile'
    });
    const resizeObserver = new ResizeObserver(debounce(() => {
      scrollInstance.resize();
      AOS.refreshHard();
    }, 250));
    resizeObserver.observe(document.body);

    // Handle all anchor links on the page
    getElements('a[href^="#"]:not([href="#"])').forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
        const targetId = link.getAttribute('href');
        if (targetId !== '#') {
          window.history.pushState(null, '', targetId);
        }
        scrollToHash(targetId);
      });
    });
    window.addEventListener('load', () => {
      if (window.location.hash) {
        scrollToHash(window.location.hash, true);
      }
    });
  }
  window.addEventListener('hashchange', handleHashChange.bind(this));
  return scrollInstance;
};

/**
 * Gets the current Lenis scroll instance
 * Useful for accessing scroll functionality from other components
 * @returns {Lenis|null} The current Lenis instance or null if not initialized
 */
export const getScroll = () => scrollInstance;